'use client';

import { useState, useEffect, useCallback } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Image from 'next/image';

interface ImageCarouselProps {
  images: string[];
  alt: string;
}

const ensureMinSlides = (images: string[], min: number = 5): string[] => {
  if (images.length >= min) return images;
  const result: string[] = [];
  while (result.length < min) {
    result.push(...images);
  }
  return result;
};

export default function ImageCarousel({ images, alt }: ImageCarouselProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'center',
    slidesToScroll: 1,
  });

  const scrollPrev = useCallback(() => {
    emblaApi?.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    emblaApi?.scrollNext();
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      setCurrentSlide(emblaApi.selectedScrollSnap());
    };

    onSelect();
    emblaApi.on('select', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi]);

  if (!images || images.length === 0) {
    return null;
  }

  const carouselImages = ensureMinSlides(images);

  return (
    <div className="relative">
      <div className="flex items-center gap-2 sm:gap-4 lg:gap-4 xl:gap-6 pb-4">
        <button
          onClick={scrollPrev}
          className="flex-shrink-0 w-6 h-6 flex items-center justify-center hover:scale-110 active:scale-90 transition-all duration-200 z-10"
          aria-label="Previous slide"
        >
          <Image
            src="/images/icons/icon-next.svg"
            alt="Previous"
            width={32}
            height={32}
            className="w-6 h-6 object-contain rotate-90"
            style={{ filter: 'brightness(0.133)' }}
            unoptimized
          />
        </button>

        <div className="embla-hsrc flex-1 overflow-hidden" ref={emblaRef}>
          <div className="embla-hsrc__container">
            {carouselImages.map((src, index) => (
              <div
                key={index}
                className="embla-hsrc__slide relative min-w-0 flex-[0_0_100%] lg:flex-[0_0_33.33%]"
              >
                <div
                  className={`aspect-[16/9] relative transition-all duration-300 ${
                    emblaApi && currentSlide === index
                      ? "scale-100 opacity-100"
                      : "scale-85 opacity-70"
                  }`}
                  style={{ borderRadius: '1rem', overflow: 'hidden' }}
                >
                  <img src={src} alt={`${alt} - Image ${index + 1}`} className="w-full h-full object-cover" />
                </div>
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={scrollNext}
          className="flex-shrink-0 w-6 h-6 flex items-center justify-center hover:scale-110 active:scale-90 transition-all duration-200 z-10"
          aria-label="Next slide"
        >
          <Image
            src="/images/icons/icon-next.svg"
            alt="Next"
            width={32}
            height={32}
            className="w-6 h-6 object-contain -rotate-90"
            style={{ filter: 'brightness(0.133)' }}
            unoptimized
          />
        </button>
      </div>

      <div className="flex justify-center items-center gap-2 mt-4 sm:mt-6 lg:mt-8">
        {images.map((_, index) => (
          <button
            key={index}
            onClick={() => emblaApi?.scrollTo(index)}
            className={`w-2 h-2 rounded-full transition-colors ${
              index === currentSlide % images.length ? "bg-honda-red" : "bg-gray-400"
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
}
